export const altoSaxophoneSyllabus = {
  1: {
    id: 1,
    title: "Level 1 — Initial Basic (Foundation Level)",
    objective:
      "To establish instrument familiarity, correct posture and embouchure, basic breath control, pitch recognition, rhythmic awareness, and fundamental musical understanding on the saxophone.",
    practicalSections: [
      {
        title: "Instrument fundamentals",
        items: [
          "Parts of the saxophone (mouthpiece, reed, ligature, neck, body, keys, bell)",
          "Types of saxophone (conceptual): Alto / Tenor / Soprano / Baritone",
          "Correct posture: Standing and sitting position",
          "Neck strap adjustment",
          "Embouchure basics: Mouthpiece placement, lip position, jaw relaxation",
          "Breathing fundamentals: Diaphragmatic breathing, steady airflow",
        ],
      },
      {
        title: "Notes & playing",
        items: [
          "Assembling and caring for the instrument",
          "Producing a clear, stable tone",
          "Long-tone exercises (basic)",
          "Playing notes using correct fingering",
          "Simple melodic phrases",
          "Awareness of intonation (high/low pitch)",
        ],
      },
      {
        title: "Scales (6)",
        items: [
          "C Major, G Major, D Major",
          "A Minor (Natural), E Minor (Natural)",
          "F Major",
          "One octave, comfortable range for the instrument",
        ],
      },
      {
        title: "Articulation techniques",
        items: [
          "Introduction to tonguing",
          "Legato vs tongued notes",
          "Starting and stopping notes cleanly",
          "Breath control between phrases",
        ],
      },
      {
        title: "Rhythm & timing",
        items: [
          "Whole, half, quarter notes",
          "Simple rhythmic patterns",
          "Playing with steady pulse",
          "Time signatures: 4/4 and 3/4",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "2 pieces: One learner's choice (any language / genre / style)",
          "One technical or study piece (teacher / examiner guided)",
          "Self-composed melody permitted (8–16 bars within the learned scale range)",
        ],
      },
    ],
    theorySections: [
      {
        title: "Music fundamentals",
        items: [
          "What is music",
          "Sound vs silence",
          "Pitch and duration",
          "Breath and tone production concepts",
        ],
      },
      {
        title: "Notes & rhythm",
        items: [
          "Musical alphabet (A–G)",
          "Basic note values (whole, half, quarter)",
          "Rests (whole, half, quarter)",
          "Counting simple rhythms",
        ],
      },
      {
        title: "Intervals",
        items: [
          "Whole step / Half step (tone / semitone concept)",
        ],
      },
      {
        title: "Scales",
        items: [
          "Concept of major and minor scales",
          "Ascending and descending scales",
          "Structure of the major scale (W–W–H–W–W–W–H)",
        ],
      },
      {
        title: "Harmony (conceptual)",
        items: [
          "What is harmony",
          "Melody vs harmony",
          "Playing one note at a time in an ensemble",
        ],
      },
      {
        title: "Notation (optional)",
        items: [
          "Basic staff notation OR fingering charts and note names",
          "Bar lines and measures",
          "Time signatures: 2/4, 3/4, 4/4",
        ],
      },
    ],
  },
  2: {
    id: 2,
    title: "Level 2 — Intermediate (Developing Level)",
    objective:
      "To develop improved tone consistency, controlled embouchure, accurate fingering, basic articulation control, rhythmic stability, and confident performance on the Alto Saxophone, while expanding musical understanding and expressive ability.",
    practicalSections: [
      {
        title: "Instrument & technique development",
        items: [
          "Review of correct posture and neck-strap adjustment",
          "Refined embouchure: Balanced lip pressure, jaw stability",
          "Improved breath support: Sustained airflow, controlled breathing between phrases",
          "Finger coordination and smooth key transitions",
          "Consistent tone across registers (low to mid range)",
        ],
      },
      {
        title: "Notes & playing",
        items: [
          "Expanded note range (low notes to middle register)",
          "Playing melodies across connected notes",
          "Long-tone exercises with dynamic control",
          "Simple melodic phrasing with breath planning",
          "Awareness of intonation using reference pitch",
        ],
      },
      {
        title: "Scales (6)",
        items: [
          "F Major, B♭ Major, E♭ Major",
          "D Minor (Natural), G Minor (Natural), A Minor (Natural)",
          "One octave, appropriate Alto Saxophone range",
        ],
      },
      {
        title: "Articulation techniques",
        items: [
          "Single tonguing control",
          "Legato tonguing",
          "Smooth note connections",
          "Clean articulation at moderate tempos",
        ],
      },
      {
        title: "Rhythm & timing",
        items: [
          "Whole, half, quarter, and eighth notes",
          "Simple rhythmic combinations",
          "Playing with a steady tempo",
          "Introduction to metronome practice",
          "Time signatures: 2/4, 3/4, 4/4",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "3 pieces: One learner's choice (any language / genre / style)",
          "One melodic piece focusing on phrasing",
          "One technical or étude-style piece",
          "Simple scale-based improvisation permitted",
          "Original melody allowed (12–16 bars)",
        ],
      },
    ],
    theorySections: [
      {
        title: "Music fundamentals",
        items: [
          "Review of sound production on wind instruments",
          "Tone quality and breath control",
          "Pitch awareness and tuning concept",
        ],
      },
      {
        title: "Notes & rhythm",
        items: [
          "Note values up to eighth notes",
          "Rests (whole, half, quarter, eighth)",
          "Strong and weak beats",
          "Counting and clapping rhythms",
        ],
      },
      {
        title: "Intervals",
        items: [
          "Whole step and half step",
          "Ascending and descending intervals",
          "Interval recognition by ear (basic)",
        ],
      },
      {
        title: "Scales",
        items: [
          "Major and natural minor scales",
          "Relative major and minor scales",
          "Scale degrees (tonic to octave)",
        ],
      },
      {
        title: "Harmony (conceptual)",
        items: [
          "Melody vs harmony in ensemble playing",
          "Introduction to chord symbols (concept only)",
          "Role of the saxophone in group settings",
        ],
      },
      {
        title: "Notation (optional)",
        items: [
          "Reading basic staff notation OR fingering charts with rhythmic values",
          "Bar lines, repeat signs",
          "Time signatures: 2/4, 3/4, 4/4",
        ],
      },
    ],
  },
  3: {
    id: 3,
    title: "Level 3 — Advanced 1 (Musical Expansion Level)",
    objective:
      "To develop secure tone across registers, improved articulation control, expanded rhythmic vocabulary, expressive phrasing, and confident musical performance on the Alto Saxophone, preparing the learner for advanced technical and stylistic work.",
    practicalSections: [
      {
        title: "Instrument & technique development",
        items: [
          "Stable posture with relaxed body alignment",
          "Consistent embouchure across low and middle registers",
          "Controlled breath support for longer phrases",
          "Improved finger agility and coordination",
          "Smooth register transitions",
        ],
      },
      {
        title: "Notes & playing",
        items: [
          "Extended range (low to upper-middle register)",
          "Melodic lines covering wider intervals",
          "Long-tone exercises with crescendo and diminuendo",
          "Expressive phrasing and dynamic contrast (p, mf, f)",
          "Intonation control using tuner or drone",
        ],
      },
      {
        title: "Scales (6)",
        items: [
          "A♭ Major, D♭ Major, G Major",
          "E Minor (Harmonic), D Minor (Natural), C Minor (Natural)",
          "One to two octaves, as appropriate",
        ],
      },
      {
        title: "Articulation techniques",
        items: [
          "Controlled single tonguing at varied tempos",
          "Legato articulation across scale passages",
          "Introduction to accented notes",
          "Clean articulation in melodic phrases",
        ],
      },
      {
        title: "Rhythm & timing",
        items: [
          "Eighth-note groupings",
          "Triplets",
          "Introduction to compound time",
          "Playing with metronome at varying tempos",
          "Time signatures: 4/4, 3/4, 6/8",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "3 pieces: One lyrical or expressive piece",
          "One rhythm-focused piece",
          "One contrasting style or tempo",
          "Self-arranged piece permitted",
          "Original composition allowed (16–24 bars)",
          "Simple improvisation on a given scale or rhythm",
        ],
      },
    ],
    theorySections: [
      {
        title: "Music fundamentals",
        items: [
          "Review of tone production and airflow",
          "Expressive elements in wind performance",
        ],
      },
      {
        title: "Notes & rhythm",
        items: [
          "Triplets and compound rhythms",
          "Accent markings and articulation symbols",
          "Phrase grouping in rhythm",
        ],
      },
      {
        title: "Intervals",
        items: [
          "Major, minor, and perfect intervals",
          "Interval recognition by ear and on instrument",
        ],
      },
      {
        title: "Scales",
        items: [
          "Major scales (up to two octaves)",
          "Natural and harmonic minor scales",
          "Ascending and descending patterns",
        ],
      },
      {
        title: "Harmony",
        items: [
          "Chord functions (I, IV, V)",
          "Relationship between melody and harmony",
          "Harmonic movement (conceptual)",
        ],
      },
      {
        title: "Musical form",
        items: [
          "Phrase structure",
          "Binary and ternary forms (listening and recognition)",
        ],
      },
      {
        title: "Notation (optional)",
        items: [
          "Intermediate-level staff notation OR articulation markings and dynamics",
          "Repeat signs and first/second endings",
        ],
      },
    ],
  },
  4: {
    id: 4,
    title: "Level 4 — Advanced 2 (Artistic Control Level)",
    objective:
      "To achieve confident control across registers, advanced articulation, stylistic awareness, expressive dynamics, and musical maturity on the Alto Saxophone, enabling performance of extended and stylistically varied repertoire.",
    practicalSections: [
      {
        title: "Instrument & technique development",
        items: [
          "Secure posture with relaxed body alignment",
          "Consistent embouchure across full playable range",
          "Advanced breath support for long phrases",
          "Improved finger agility and technical precision",
          "Smooth register transitions and control",
        ],
      },
      {
        title: "Notes & playing",
        items: [
          "Expanded range including upper register",
          "Melodies spanning wide intervals",
          "Dynamic shaping within phrases",
          "Expressive use of tone color",
          "Controlled intonation across registers",
        ],
      },
      {
        title: "Scales (6)",
        items: [
          "E Major, B Major, F♯ Major",
          "C♯ Minor (Harmonic), F Minor (Natural), B♭ Minor (Natural)",
          "Two octaves where possible, depending on range",
        ],
      },
      {
        title: "Articulation techniques",
        items: [
          "Advanced single tonguing",
          "Introduction to double tonguing (conceptual)",
          "Accented articulation and ghost notes",
          "Smooth legato articulation in fast passages",
        ],
      },
      {
        title: "Rhythm & timing",
        items: [
          "Compound rhythms (6/8, 9/8)",
          "Syncopated rhythmic patterns",
          "Tempo changes within pieces",
          "Rhythmic precision with metronome",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "4 pieces: At least two contrasting styles (e.g., classical, jazz, popular)",
          "One technically demanding piece",
          "One expressive or lyrical piece",
          "Self-arranged work permitted",
          "Original composition allowed (24–32 bars)",
          "Structured improvisation over scale or chord pattern",
        ],
      },
    ],
    theorySections: [
      {
        title: "Scales & modes",
        items: [
          "Harmonic and melodic minor scales",
          "Introduction to modes (sound and concept)",
        ],
      },
      {
        title: "Harmony",
        items: [
          "Cadences (perfect, imperfect, plagal)",
          "Secondary dominants (conceptual)",
          "Tension and resolution",
        ],
      },
      {
        title: "Musical form",
        items: [
          "Binary, ternary, and rondo forms",
          "Thematic development",
        ],
      },
      {
        title: "Style & interpretation",
        items: [
          "Articulation styles across genres",
          "Phrasing differences in classical vs jazz contexts",
        ],
      },
      {
        title: "Notation (optional)",
        items: [
          "Advanced staff notation",
          "Articulation symbols, dynamics, and expressive markings",
          "Da capo, dal segno, and coda symbols",
        ],
      },
    ],
  },
  5: {
    id: 5,
    title: "Level 5 — Professional (Performance & Musical Authority)",
    objective:
      "To demonstrate professional-level technical command, stylistic versatility, expressive maturity, advanced articulation, improvisational fluency, and deep theoretical understanding on the Alto Saxophone, suitable for public performance and professional study.",
    practicalSections: [
      {
        title: "Instrument & technique development",
        items: [
          "Fluent control across full instrument range",
          "Stable embouchure with flexibility for expression",
          "Advanced breath support and phrase shaping",
          "High-speed finger coordination and accuracy",
          "Endurance and consistency across extended performance",
        ],
      },
      {
        title: "Notes & playing",
        items: [
          "Complex melodic and technical passages",
          "Fast passagework with clarity and precision",
          "Stylistic interpretation across genres",
          "Confident ensemble and accompaniment playing",
          "Advanced intonation control in all registers",
        ],
      },
      {
        title: "Scales (complete mastery)",
        items: [
          "All Major Scales (two to three octaves)",
          "All Natural Minor Scales",
          "Harmonic Minor Scales (all keys)",
          "Melodic Minor Scales",
          "Major & Minor Pentatonic Scales",
          "Blues Scales",
        ],
      },
      {
        title: "Articulation techniques",
        items: [
          "Advanced single tonguing at high tempos",
          "Double tonguing (functional use)",
          "Subtone (conceptual / stylistic)",
          "Articulation variation for stylistic demands",
        ],
      },
      {
        title: "Rhythm & timing",
        items: [
          "Irregular meters (5/4, 7/8)",
          "Polyrhythmic awareness",
          "Rubato and tempo modulation",
          "Ensemble rhythmic precision",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "5 pieces: One technically virtuosic piece",
          "One extended-form piece",
          "One original composition (mandatory)",
          "Multiple contrasting genres or styles",
          "Advanced improvisation over harmonic progressions",
          "Performance with accompaniment or ensemble",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced harmony",
        items: [
          "Modulation techniques",
          "Chord function and substitutions",
          "Voice leading concepts",
        ],
      },
      {
        title: "Improvisation & style",
        items: [
          "Scale–chord relationships",
          "Stylistic vocabulary (jazz, classical, contemporary)",
          "Motivic development in improvisation",
        ],
      },
      {
        title: "Composition & arrangement",
        items: [
          "Writing idiomatically for saxophone",
          "Arranging melodies for ensembles",
        ],
      },
      {
        title: "Musical analysis",
        items: [
          "Structural and harmonic analysis of repertoire",
          "Comparative style analysis",
        ],
      },
    ],
  },
  6: {
    id: 6,
    title: "Level 6 — Mastery (Artist / Educator Level)",
    objective:
      "To certify artistic authority, complete technical freedom, original creative voice, pedagogical understanding, and conceptual mastery on the Alto Saxophone, qualifying the candidate as a performer, composer, arranger, or educator.",
    practicalSections: [
      {
        title: "Performance & artistry",
        items: [
          "Full recital performance (30–45 minutes)",
          "Multi-genre and multi-style repertoire",
          "Original compositions (mandatory)",
          "Advanced improvisation across harmonic contexts",
          "Ensemble and/or accompaniment performance",
          "Demonstration of interpretative depth and personal artistic identity",
        ],
      },
      {
        title: "Technical mastery",
        items: [
          "Effortless control across the full instrument range",
          "Advanced tone color manipulation",
          "Extreme dynamic control",
          "Precision articulation at all tempi",
          "Professional-level endurance and consistency",
        ],
      },
      {
        title: "Creative & professional skills",
        items: [
          "Improvisation in multiple styles",
          "Spontaneous musical interaction",
          "Interpretation of complex scores or lead sheets",
          "Professional rehearsal and performance readiness",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced musical studies",
        items: [
          "Advanced harmonic, modal, and formal analysis",
          "Stylistic evolution of the saxophone",
          "Advanced rhythm studies and metric modulation",
        ],
      },
      {
        title: "Pedagogy & methodology",
        items: [
          "Alto saxophone teaching techniques",
          "Embouchure development strategies",
          "Practice methodology and curriculum design",
          "Assessment and learner progression",
        ],
      },
      {
        title: "Composition, arrangement & research",
        items: [
          "Advanced composition for saxophone",
          "Arranging for saxophone ensembles",
          "Research project, dissertation, or lecture-recital (optional)",
        ],
      },
      {
        title: "Aesthetics & professional practice",
        items: [
          "Music aesthetics and philosophy",
          "Performance psychology",
          "Artistic planning and career development",
        ],
      },
    ],
  },
};
